import { uniqueIcaos } from "./weather";
import type { TfrNotice } from "./tfr";

/**
 * Public NOTAM list: CFPS login-free JSON first (Canada-capable), then
 * AviationAPI (best-effort). Official FAA NOTAM API is optional (US).
 * CheckWX public docs list Station / METAR / TAF only — no NOTAM product.
 * Never invent NOTAM copy.
 */
export const NOTAM_NEEDS_SOURCE =
  "Public CFPS / AviationAPI lists did not answer. Optional FAA NOTAM API keys (US) can be set under Settings. CheckWX does not offer NOTAMs. This desk does not invent NOTAM copy.";

/** US ICAOs with no FAA Client ID/Secret — fail closed with a Settings hint. */
export const NOTAM_NEEDS_FAA =
  "US airport NOTAMs need FAA Client ID and Secret under Settings → Integrations / Secrets. Canada CFPS still works without a key. CheckWX is METAR/TAF only — not NOTAMs. Nothing invented.";

export const NOTAM_UNAVAILABLE =
  "NOTAM list unavailable. Nothing invented. TFR identifiers may still appear from the TFR feed.";

export const NOTAM_FILTER_NOTE =
  "Showing flight-affecting NOTAMs only (runway/taxiway closures, airport closed, significant lighting/navaid outages). Raw dump omitted.";

/**
 * FAA NOTAM API covers US locations (CONUS K…, Alaska/Hawaii/Pacific P…,
 * Caribbean T…). Canada C… stays on CFPS.
 */
export function isUsNotamIcao(icao: string | null | undefined): boolean {
  const code = (icao ?? "").trim().toUpperCase();
  if (!/^[A-Z][A-Z0-9]{3}$/.test(code)) return false;
  return code.startsWith("K") || code.startsWith("P") || code.startsWith("T");
}

/** NAV CANADA CFPS alphanumeric weather/NOTAM JSON. Same path as login-free wxrecall. */
export const CFPS_NOTAM_HOST = "https://plan.navcanada.ca";

/** AviationAPI public host. No key. Often DNS / HTTP 530. Desk still tries. */
export const AVIATIONAPI_NOTAM_HOST = "https://api.aviationapi.com";

/** Official FAA NOTAM API. Client id + secret from api.faa.gov. */
export const FAA_NOTAM_API_HOST = "https://external-api.faa.gov";

/** CheckWX weather host. Public docs have no NOTAM product; desk does not call /notam. */
export const CHECKWX_NOTAM_HOST = "https://api.checkwx.com";

/**
 * Source + rate-limit notes for Settings / docs. Not shown as live NOTAM text.
 *
 * - CFPS `GET /weather/api/alpha/?site=ICAO&alpha=notam` — no key. Same JSON
 *   the official login-free Weather and NOTAM tab calls. Canada-capable.
 *   NAV CANADA does not publish a developer API; commercial data sales exist.
 *   Dep/dest only, 10 min cache. Not an HTML scrape.
 * - AviationAPI `GET /v1/notam?apt=ICAO,ICAO` — no key. Often DNS / HTTP 530.
 *   Desk still tries for US / other ICAOs. Never invents.
 * - CheckWX — Station / METAR / TAF only on current public docs/pricing.
 *   Desk does not fetch `/notam/{icao}`.
 * - FAA `GET /notamapi/v1/notams?icaoLocation=&responseFormat=geoJson` —
 *   Settings client id+secret headers (no OAuth token mint). US locations.
 * Desk fetches dep/dest only, caches 10 minutes, and filters to ops impact.
 */
export const NOTAM_SOURCE_LIMITS = {
  cfps: {
    host: CFPS_NOTAM_HOST,
    path: "/weather/api/alpha/",
    auth: "none",
    cacheMin: 10,
    rate: "Same JSON as login-free CFPS wxrecall. Dep/dest only. Cache 10 min. Canada-capable.",
  },
  public: {
    host: AVIATIONAPI_NOTAM_HOST,
    path: "/v1/notam",
    auth: "none",
    cacheMin: 10,
    rate: "Often DNS / HTTP 530. Treat 429 as unavailable. Cache 10 min. Dep/dest only.",
  },
  checkwx: {
    host: CHECKWX_NOTAM_HOST,
    path: "/notam/{icao}",
    auth: "unused — no public NOTAM product",
    cacheMin: 10,
    rate: "CheckWX public docs list Station / METAR / TAF only. Desk does not call this URL.",
  },
  faa: {
    host: FAA_NOTAM_API_HOST,
    path: "/notamapi/v1/notams",
    auth: "client_id + client_secret headers",
    cacheMin: 10,
    rate: "Per FAA developer portal. responseFormat=geoJson. One GET per US ICAO.",
  },
} as const;

export type NotamSourceId = "cfps" | "public" | "checkwx" | "faa" | "tfr-feed" | "last-known" | "none";

/** Ops-impact bucket used on trip / plan chips. Empty = not flight-affecting. */
export type NotamImpact = "rwy" | "twy" | "ad" | "lighting" | "navaid";

export const NOTAM_IMPACT_LABEL: Record<NotamImpact, string> = {
  rwy: "RWY",
  twy: "TWY",
  ad: "AD",
  lighting: "LGT",
  navaid: "NAV",
};

export type NotamNotice = {
  id: string;
  icao: string;
  text: string;
  source: NotamSourceId;
  impact?: NotamImpact;
};

export type NotamBrief = {
  status: "ok" | "unavailable" | "last-known" | "needs-source";
  note: string;
  /** TFR NOTAM identifiers from the TFR payload — not invented airport NOTAMs. */
  fromTfr: NotamNotice[];
  /** Flight-affecting rows only. Never a raw dump. */
  items: NotamNotice[];
  /** Count before the ops-impact filter. */
  rawCount?: number;
  omittedCount?: number;
  source?: NotamSourceId;
  fetchedAt?: number;
};

/** Last successful ops brief. Empty items still count when fetchedAt is set. */
export type NotamLastKnown = {
  items: NotamNotice[];
  fetchedAt: number;
  rawCount?: number;
  omittedCount?: number;
};

const NOTAM_STATUSES: ReadonlySet<NotamBrief["status"]> = new Set([
  "ok",
  "unavailable",
  "last-known",
  "needs-source",
]);

const NOTAM_SOURCES: ReadonlySet<NotamSourceId> = new Set([
  "cfps",
  "public",
  "checkwx",
  "faa",
  "tfr-feed",
  "last-known",
  "none",
]);

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value).trim();
  }
  return "";
}

function coreNotam(rec: Record<string, unknown>): Record<string, unknown> | null {
  const props = asRecord(rec.properties);
  const core = asRecord(props?.coreNOTAMData) ?? asRecord(rec.coreNOTAMData);
  return asRecord(core?.notam) ?? asRecord(rec.notam);
}

/** CFPS `text` is a JSON string `{ raw, english, french }` — never invent copy. */
function unwrapCfpsText(value: unknown): { text: string; id: string; icao: string } {
  const rawField = asText(value);
  let blob = rawField;
  const inner = rawField.startsWith("{") ? asRecord(safeJson(rawField)) : asRecord(value);
  if (inner) {
    blob = asText(inner.raw) || asText(inner.english) || asText(inner.french) || rawField;
  }
  const idMatch = blob.match(/\b([A-Z]\d{3,5}\/\d{2})\b/);
  const icaoMatch = blob.match(/\bA\)\s*([A-Z]{4})\b/);
  return { text: blob, id: idMatch?.[1] ?? "", icao: icaoMatch?.[1] ?? "" };
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function noticeFromRecord(rec: Record<string, unknown>, fallbackIcao = "", source: NotamSourceId = "public"): NotamNotice | null {
  const core = coreNotam(rec);
  const cfps = unwrapCfpsText(rec.text);
  const text =
    cfps.text ||
    asText(rec.notam) ||
    asText(rec.message) ||
    asText(rec.body) ||
    asText(rec.traditional_message) ||
    asText(rec.icao_message) ||
    asText(rec.traditionalMessage) ||
    asText(rec.icaoMessage) ||
    asText(core?.text) ||
    asText(core?.icaoMessage) ||
    asText(core?.traditionalMessage);
  const id =
    asText(rec.id) ||
    asText(rec.notam_id) ||
    asText(rec.notamId) ||
    asText(rec.notam_number) ||
    asText(rec.key) ||
    asText(rec.number) ||
    asText(core?.id) ||
    asText(core?.number) ||
    cfps.id ||
    asText(rec.pk);
  if (!text && !id) return null;
  const icao = (
    asText(rec.icao) ||
    asText(rec.airport) ||
    asText(rec.ident) ||
    asText(rec.facility_id) ||
    asText(rec.icaoLocation) ||
    asText(rec.location) ||
    asText(core?.icaoLocation) ||
    asText(core?.location) ||
    asText(asRecord(rec.position)?.pointReference) ||
    cfps.icao ||
    fallbackIcao
  ).toUpperCase();
  const notice: NotamNotice = {
    id: id || text.slice(0, 24),
    icao,
    text,
    source,
  };
  const impact = classifyNotamImpact(notice);
  return impact ? { ...notice, impact } : notice;
}

function listFromRoot(root: Record<string, unknown> | null, raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (!root) return [];
  if (Array.isArray(root.notams)) return root.notams;
  if (Array.isArray(root.data)) return root.data;
  if (Array.isArray(root.items)) return root.items;
  if (Array.isArray(root.features)) return root.features;
  if (Array.isArray(root.pageItems)) return root.pageItems;
  return [];
}

/**
 * AviationAPI `{ ICAO: [ { notam_id, text } ] }`, CFPS `{ data: [{ location, text }] }`
 * (`text` may be a `{ raw, english, french }` JSON string), CheckWX `{ data }`,
 * FAA GeoJSON / `{ items: [{ properties.coreNOTAMData.notam }] }`, or a flat list.
 * Empty / error → []. Never fabricates NOTAM copy.
 */
export function parseGetNotams(raw: unknown, source: NotamSourceId = "public"): NotamNotice[] {
  if (raw == null) return [];
  const root = asRecord(raw);
  if (root) {
    const err = asText(root.error) || asText(root.message);
    if (err && !root.notams && !root.data && !root.features && !root.items && !root.pageItems) return [];
  }

  const out: NotamNotice[] = [];
  const seen = new Set<string>();

  const push = (notice: NotamNotice | null) => {
    if (!notice) return;
    const key = `${notice.id}|${notice.text}|${notice.icao}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(notice);
  };

  const list = listFromRoot(root, raw);
  if (list.length) {
    for (const row of list) {
      const rec = asRecord(row);
      if (rec) push(noticeFromRecord(rec, "", source));
    }
    return out;
  }

  if (root) {
    for (const [key, value] of Object.entries(root)) {
      if (key === "error" || key === "message" || key === "status" || key === "results") continue;
      if (!Array.isArray(value)) continue;
      const icao = key.trim().toUpperCase();
      for (const row of value) {
        const rec = asRecord(row);
        if (rec) push(noticeFromRecord(rec, icao, source));
      }
    }
  }

  return out;
}

export function buildPublicNotamUrl(icaos: string[], host = AVIATIONAPI_NOTAM_HOST): string {
  const apt = uniqueIcaos(icaos, 8).join(",");
  const params = new URLSearchParams({ apt });
  return `${host.replace(/\/$/, "")}/v1/notam?${params.toString()}`;
}

/** Same query string the official login-free CFPS wxrecall UI builds. */
export function buildCfpsNotamUrl(icaos: string[], host = CFPS_NOTAM_HOST): string {
  const params = new URLSearchParams();
  for (const site of uniqueIcaos(icaos, 8)) params.append("site", site);
  params.set("alpha", "notam");
  return `${host.replace(/\/$/, "")}/weather/api/alpha/?${params.toString()}`;
}

export function buildFaaNotamUrl(icao: string, host = FAA_NOTAM_API_HOST): string {
  const loc = uniqueIcaos([icao], 1)[0] ?? "";
  // responseFormat=geoJson is required — default AIXM/XML is not parseable here.
  const params = new URLSearchParams({
    icaoLocation: loc,
    responseFormat: "geoJson",
    pageSize: "50",
    pageNum: "1",
  });
  return `${host.replace(/\/$/, "")}/notamapi/v1/notams?${params.toString()}`;
}

export function buildCheckWxNotamUrl(icao: string, host = CHECKWX_NOTAM_HOST): string {
  const loc = uniqueIcaos([icao], 1)[0] ?? "";
  return `${host.replace(/\/$/, "")}/notam/${encodeURIComponent(loc)}`;
}

export type NotamAttempt = {
  source: NotamSourceId;
  ok: boolean;
  items: NotamNotice[];
  error?: string;
};

function sourceLabel(source: NotamSourceId): string {
  if (source === "cfps") return "CFPS (NAV CANADA)";
  if (source === "public") return "public list (AviationAPI / ICAO-style)";
  if (source === "checkwx") return "CheckWX";
  if (source === "faa") return "FAA NOTAM API";
  if (source === "last-known") return "last-known";
  return source;
}

function mergeNotamItems(attempts: NotamAttempt[]): { items: NotamNotice[]; sources: NotamSourceId[] } {
  const items: NotamNotice[] = [];
  const seen = new Set<string>();
  const sources: NotamSourceId[] = [];
  for (const attempt of attempts) {
    if (!attempt.ok || !attempt.items.length) continue;
    if (!sources.includes(attempt.source)) sources.push(attempt.source);
    for (const notice of attempt.items) {
      const key = `${notice.id}|${notice.text}|${notice.icao}`;
      if (seen.has(key)) continue;
      seen.add(key);
      items.push(notice);
    }
  }
  return { items, sources };
}

function okNote(sources: NotamSourceId[], rawCount: number, kept: number): string {
  const from = sources.map(sourceLabel).join(" + ");
  if (kept) {
    return `${kept} flight-affecting NOTAM(s) of ${rawCount} from ${from}. ${NOTAM_FILTER_NOTE} Not invented.`;
  }
  if (rawCount) {
    return `${from} answered (${rawCount} NOTAM(s)); none are flight-affecting. ${NOTAM_FILTER_NOTE} Nothing invented.`;
  }
  return `${from || "NOTAM list"} answered with no items for these ICAOs. Nothing invented.`;
}

export type SelectNotamFallbackOpts = {
  /** ICAOs requested for this brief — used for US fail-closed FAA hints. */
  icaos?: string[];
};

export function selectNotamFallback(
  attempts: NotamAttempt[],
  fromTfr: NotamNotice[] = [],
  lastKnown?: NotamLastKnown | null,
  opts: SelectNotamFallbackOpts = {},
): NotamBrief {
  const merged = mergeNotamItems(attempts);
  const wantsUs = uniqueIcaos(opts.icaos ?? []).some(isUsNotamIcao);
  const faaAttempt = attempts.find((a) => a.source === "faa");
  const faaFailed = Boolean(faaAttempt && !faaAttempt.ok);
  const needsFaa = wantsUs && !faaAttempt;
  // Empty CFPS / AviationAPI "ok" must not hide a failed FAA attempt, and must not
  // close a US-only brief when FAA credentials were never tried.
  const liveOk =
    attempts.some((a) => a.ok) && !faaFailed && !(needsFaa && merged.items.length === 0);

  if (merged.items.length || liveOk) {
    const affecting = filterFlightAffectingNotams(merged.items);
    const sources = merged.sources.length ? merged.sources : attempts.filter((a) => a.ok).map((a) => a.source);
    return {
      status: "ok",
      note: okNote(sources, merged.items.length, affecting.length),
      fromTfr,
      items: affecting,
      rawCount: merged.items.length,
      omittedCount: Math.max(0, merged.items.length - affecting.length),
      source: sources[0] ?? "public",
      fetchedAt: Date.now(),
    };
  }
  if (lastKnown && Number.isFinite(lastKnown.fetchedAt) && lastKnown.fetchedAt > 0) {
    const affecting = filterFlightAffectingNotams(lastKnown.items);
    const rawCount = lastKnown.rawCount ?? lastKnown.items.length;
    return {
      status: "last-known",
      note: `NOTAM list unavailable. Showing last known ${new Date(lastKnown.fetchedAt).toISOString()}. ${NOTAM_FILTER_NOTE} Nothing invented.`,
      fromTfr,
      items: affecting,
      rawCount,
      omittedCount: lastKnown.omittedCount ?? Math.max(0, rawCount - affecting.length),
      source: "last-known",
      fetchedAt: lastKnown.fetchedAt,
    };
  }
  const faaErr = faaAttempt?.error?.trim() || "";
  const err = faaErr || attempts.map((a) => a.error).filter(Boolean)[0] || "";
  if (faaAttempt) {
    return {
      status: "unavailable",
      note: err ? `${err} ${NOTAM_UNAVAILABLE}` : NOTAM_UNAVAILABLE,
      fromTfr,
      items: [],
      rawCount: 0,
      omittedCount: 0,
      source: "none",
    };
  }
  const hint = wantsUs ? NOTAM_NEEDS_FAA : NOTAM_NEEDS_SOURCE;
  return {
    status: "needs-source",
    note: err ? `${err} ${hint}` : hint,
    fromTfr,
    items: [],
    rawCount: 0,
    omittedCount: 0,
    source: "none",
  };
}

/** Surface TFR NOTAM ids as identifiers only — no extra sentences invented. */
export function notamsFromTfrs(tfrs: TfrNotice[]): NotamNotice[] {
  const seen = new Set<string>();
  const out: NotamNotice[] = [];
  for (const tfr of tfrs) {
    const id = tfr.id.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      icao: tfr.facility,
      text: tfr.text,
      source: "tfr-feed",
    });
  }
  return out;
}

export function emptyNotamBrief(fromTfr: NotamNotice[] = []): NotamBrief {
  return {
    status: "unavailable",
    note: NOTAM_UNAVAILABLE,
    fromTfr,
    items: [],
    rawCount: 0,
    omittedCount: 0,
    source: "none",
  };
}

function hydrateSource(value: unknown): NotamSourceId | undefined {
  const raw = asText(value);
  return NOTAM_SOURCES.has(raw as NotamSourceId) ? (raw as NotamSourceId) : undefined;
}

/** Reconstruct a notice from a persisted / snapshot row. Never invents text. */
export function hydrateNotamNotice(raw: unknown, fallbackSource: NotamSourceId = "last-known"): NotamNotice | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  return noticeFromRecord(rec, "", hydrateSource(rec.source) ?? fallbackSource);
}

/**
 * Snapshot / persist hydrate. Flight-affecting items only. Null when the
 * payload is missing or not a brief — never fabricates NOTAM copy.
 */
export function hydrateNotamBrief(raw: unknown): NotamBrief | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  const status = asText(rec.status) as NotamBrief["status"];
  if (!NOTAM_STATUSES.has(status)) return null;
  const source = hydrateSource(rec.source) ?? "none";
  const items = filterFlightAffectingNotams(
    (Array.isArray(rec.items) ? rec.items : []).flatMap((row) => {
      const notice = hydrateNotamNotice(row, source === "none" ? "last-known" : source);
      return notice ? [notice] : [];
    }),
  );
  const fromTfr = (Array.isArray(rec.fromTfr) ? rec.fromTfr : []).flatMap((row) => {
    const notice = hydrateNotamNotice(row, "tfr-feed");
    return notice ? [{ ...notice, source: "tfr-feed" as const }] : [];
  });
  const fetchedAt = typeof rec.fetchedAt === "number" && Number.isFinite(rec.fetchedAt) ? rec.fetchedAt : undefined;
  const rawCount = typeof rec.rawCount === "number" && Number.isFinite(rec.rawCount) ? rec.rawCount : items.length;
  const omittedCount =
    typeof rec.omittedCount === "number" && Number.isFinite(rec.omittedCount)
      ? rec.omittedCount
      : Math.max(0, rawCount - items.length);
  return {
    status,
    note: asText(rec.note) || (items.length ? okNote(source === "none" ? [] : [source], rawCount, items.length) : NOTAM_UNAVAILABLE),
    fromTfr,
    items,
    rawCount,
    omittedCount,
    source,
    fetchedAt,
  };
}

/** Seed last-known from a live or snapshot brief. Empty items still seed when fetchedAt is set. */
export function lastKnownFromBrief(brief: NotamBrief | null | undefined): NotamLastKnown | null {
  if (!brief) return null;
  if (brief.status !== "ok" && brief.status !== "last-known") return null;
  const fetchedAt = brief.fetchedAt;
  if (!fetchedAt || !Number.isFinite(fetchedAt)) return null;
  return {
    items: filterFlightAffectingNotams(brief.items),
    fetchedAt,
    rawCount: brief.rawCount,
    omittedCount: brief.omittedCount,
  };
}

/** Content hash for Nanode schedule revision — not fetchedAt (avoids a push every refresh). */
export function notamBriefFingerprint(raw: unknown): string {
  const brief = hydrateNotamBrief(raw);
  if (!brief) return "";
  return JSON.stringify({
    status: brief.status,
    rawCount: brief.rawCount ?? 0,
    items: brief.items.map((n) => `${n.id}|${n.icao}|${n.impact ?? ""}|${n.text}`),
  });
}

const CLOSED = /\b(CLSD|CLOSED|NOT AVBL|NOT AVAILABLE|UNAVAILABLE)\b/i;
const OUTAGE = /\b(U\/S|UNSERVICEABLE|OUT OF SERVICE|\bOTS\b|UNUSABLE|INOP|INOPERATIVE)\b/i;
const RWY = /\b(RWY|RWYS|RUNWAY|RUNWAYS)\b/i;
const TWY = /\b(TWY|TWYS|TAXIWAY|TAXIWAYS)\b/i;
const AD = /\b(AD CLSD|APT CLSD|AERODROME|AIRPORT CLOSED|AD CLOSED|APT CLOSED|AIRPORT CLSD)\b|\b(AD|APT|AERODROME|AIRPORT)\b/i;
const LIGHTING = /\b(ALS|PAPI|VASI|REIL|HIRL|MIRL|LIRL|RWY LGT|RUNWAY LIGHT|APCH LGT|APPROACH LIGHT|THR LGT|THRESHOLD LIGHT|TDZ LGT|SALS|MALSR|SSALR)\b/i;
const NAVAID = /\b(ILS|VOR|NDB|DME|TACAN|LOC|GP\b|GS\b|GLIDESLOPE|LOCALIZER|GBAS|LPV|GPS (?:RWY|APCH|APPROACH))\b/i;
const Q_RWY = /\bQ(?:RT|MR)(?:LC|CA|CM|LL)\b/i;
const Q_TWY = /\bQMX(?:LC|CA|LL)\b/i;
const Q_AD = /\bQFA(?:LC|LT|AH|XX)\b/i;
const Q_LGT = /\bQL[AWZ]AS\b/i;
const Q_NAV = /\bQI[A-Z]{3}\b|\bQN[VDM]AS\b/i;

/**
 * Conservative ops-impact classifier. Closures and significant lighting /
 * navaid outages only — not birds, fuel, parking, or a raw NOTAM dump.
 */
export function classifyNotamImpact(notice: Pick<NotamNotice, "id" | "text">): NotamImpact | undefined {
  const blob = `${notice.id} ${notice.text}`;
  if (Q_AD.test(blob) || (AD.test(blob) && CLOSED.test(blob))) return "ad";
  if (Q_RWY.test(blob) || (RWY.test(blob) && CLOSED.test(blob))) return "rwy";
  if (Q_TWY.test(blob) || (TWY.test(blob) && CLOSED.test(blob))) return "twy";
  if (Q_LGT.test(blob) || (LIGHTING.test(blob) && (OUTAGE.test(blob) || CLOSED.test(blob)))) return "lighting";
  if (Q_NAV.test(blob) || (NAVAID.test(blob) && (OUTAGE.test(blob) || CLOSED.test(blob)))) return "navaid";
  return undefined;
}

export function filterFlightAffectingNotams(items: NotamNotice[]): NotamNotice[] {
  const out: NotamNotice[] = [];
  const seen = new Set<string>();
  for (const notice of items) {
    const impact = notice.impact ?? classifyNotamImpact(notice);
    if (!impact) continue;
    const key = `${notice.id}|${notice.icao}|${impact}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...notice, impact });
  }
  return out;
}

/** Notices for the selected trip / plan pair (dep + dest, optional altn). */
export function notamsForIcaos(items: NotamNotice[], icaos: Array<string | undefined | null>): NotamNotice[] {
  const want = new Set(uniqueIcaos(icaos));
  if (!want.size) return [];
  return items.filter((n) => !n.icao || want.has(n.icao.trim().toUpperCase()));
}

export function notamChipLabel(notice: NotamNotice): string {
  const tag = notice.impact ? NOTAM_IMPACT_LABEL[notice.impact] : "NOTAM";
  return notice.icao ? `${notice.icao} ${tag}` : tag;
}
